import { useState } from 'react';
import { useAuth } from '../context/AuthContext';

const PAYMENT_MODES = [
  { id: 'cash', name: 'Cash', enabled: true },
  { id: 'upi', name: 'UPI', enabled: true },
  { id: 'bank', name: 'Bank Transfer', enabled: true },
  { id: 'card', name: 'Card', enabled: false },
  { id: 'cheque', name: 'Cheque', enabled: true },
];

const EXPENSE_CATEGORIES = [
  { id: 'rent', name: 'Rent', enabled: true },
  { id: 'utilities', name: 'Utilities', enabled: true },
  { id: 'salary', name: 'Salary', enabled: true },
  { id: 'marketing', name: 'Marketing', enabled: true },
  { id: 'inventory', name: 'Inventory', enabled: true },
  { id: 'transport', name: 'Transport', enabled: true },
  { id: 'misc', name: 'Miscellaneous', enabled: true },
];

export default function Settings() {
  const { user } = useAuth();
  const [businessProfile, setBusinessProfile] = useState({
    name: user?.businessName || '',
    ownerName: user?.name || '',
    email: user?.email || '',
    phone: '',
    address: '',
    gst: '',
    pan: '',
  });
  const [paymentModes, setPaymentModes] = useState(PAYMENT_MODES);
  const [expenseCategories, setExpenseCategories] = useState(EXPENSE_CATEGORIES);
  const [notifications, setNotifications] = useState({
    lowStock: true,
    duePayments: true,
    newOrders: true,
    reports: false,
  });

  const handleProfileChange = (e) => {
    const { name, value } = e.target;
    setBusinessProfile(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handlePaymentModeToggle = (id) => {
    setPaymentModes(prev =>
      prev.map(mode =>
        mode.id === id ? { ...mode, enabled: !mode.enabled } : mode
      )
    );
  };

  const handleCategoryToggle = (id) => {
    setExpenseCategories(prev =>
      prev.map(category =>
        category.id === id ? { ...category, enabled: !category.enabled } : category
      )
    );
  };

  const handleNotificationToggle = (key) => {
    setNotifications(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  return (
    <div className="container mx-auto px-4">
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Settings</h1>
        <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
          Manage your business settings and preferences
        </p>
      </div>

      <div className="grid grid-cols-1 gap-8">
        {/* Business Profile */}
        <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6">
          <h2 className="text-lg font-medium text-gray-900 dark:text-white mb-6">Business Profile</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label htmlFor="name" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Business Name
              </label>
              <input
                type="text"
                name="name"
                id="name"
                value={businessProfile.name}
                onChange={handleProfileChange}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
              />
            </div>

            <div>
              <label htmlFor="ownerName" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Owner Name
              </label>
              <input
                type="text"
                name="ownerName"
                id="ownerName"
                value={businessProfile.ownerName}
                onChange={handleProfileChange}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
              />
            </div>

            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Email
              </label>
              <input
                type="email"
                name="email"
                id="email"
                value={businessProfile.email}
                onChange={handleProfileChange}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
              />
            </div>

            <div>
              <label htmlFor="phone" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Phone
              </label>
              <input
                type="tel"
                name="phone"
                id="phone"
                value={businessProfile.phone}
                onChange={handleProfileChange}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
              />
            </div>

            <div className="md:col-span-2">
              <label htmlFor="address" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Address
              </label>
              <textarea
                name="address"
                id="address"
                rows={3}
                value={businessProfile.address}
                onChange={handleProfileChange}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
              />
            </div>

            <div>
              <label htmlFor="gst" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                GST Number
              </label>
              <input
                type="text"
                name="gst"
                id="gst"
                value={businessProfile.gst}
                onChange={handleProfileChange}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
              />
            </div>

            <div>
              <label htmlFor="pan" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                PAN Number
              </label>
              <input
                type="text"
                name="pan"
                id="pan"
                value={businessProfile.pan}
                onChange={handleProfileChange}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
              />
            </div>
          </div>
        </div>

        {/* Payment Modes */}
        <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6">
          <h2 className="text-lg font-medium text-gray-900 dark:text-white mb-6">Payment Modes</h2>
          <div className="space-y-4">
            {paymentModes.map((mode) => (
              <div key={mode.id} className="flex items-center justify-between">
                <span className="text-sm font-medium text-gray-900 dark:text-gray-300">{mode.name}</span>
                <button
                  type="button"
                  onClick={() => handlePaymentModeToggle(mode.id)}
                  className={`${
                    mode.enabled
                      ? 'bg-indigo-600'
                      : 'bg-gray-200 dark:bg-gray-700'
                  } relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2`}
                >
                  <span
                    className={`${
                      mode.enabled ? 'translate-x-5' : 'translate-x-0'
                    } pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out`}
                  />
                </button>
              </div>
            ))}
          </div>
        </div>

        {/* Expense Categories */}
        <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6">
          <h2 className="text-lg font-medium text-gray-900 dark:text-white mb-6">Expense Categories</h2>
          <div className="space-y-4">
            {expenseCategories.map((category) => (
              <div key={category.id} className="flex items-center justify-between">
                <span className="text-sm font-medium text-gray-900 dark:text-gray-300">{category.name}</span>
                <button
                  type="button"
                  onClick={() => handleCategoryToggle(category.id)}
                  className={`${
                    category.enabled
                      ? 'bg-indigo-600'
                      : 'bg-gray-200 dark:bg-gray-700'
                  } relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2`}
                >
                  <span
                    className={`${
                      category.enabled ? 'translate-x-5' : 'translate-x-0'
                    } pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out`}
                  />
                </button>
              </div>
            ))}
          </div>
        </div>

        {/* Notifications */}
        <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6">
          <h2 className="text-lg font-medium text-gray-900 dark:text-white mb-6">Notifications</h2>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <span className="text-sm font-medium text-gray-900 dark:text-gray-300">Low Stock Alerts</span>
                <p className="text-sm text-gray-500">Get notified when product stock is running low</p>
              </div>
              <button
                type="button"
                onClick={() => handleNotificationToggle('lowStock')}
                className={`${
                  notifications.lowStock
                    ? 'bg-indigo-600'
                    : 'bg-gray-200 dark:bg-gray-700'
                } relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2`}
              >
                <span
                  className={`${
                    notifications.lowStock ? 'translate-x-5' : 'translate-x-0'
                  } pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out`}
                />
              </button>
            </div>

            <div className="flex items-center justify-between">
              <div>
                <span className="text-sm font-medium text-gray-900 dark:text-gray-300">Due Payment Reminders</span>
                <p className="text-sm text-gray-500">Get notified about upcoming payment dues</p>
              </div>
              <button
                type="button"
                onClick={() => handleNotificationToggle('duePayments')}
                className={`${
                  notifications.duePayments
                    ? 'bg-indigo-600'
                    : 'bg-gray-200 dark:bg-gray-700'
                } relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2`}
              >
                <span
                  className={`${
                    notifications.duePayments ? 'translate-x-5' : 'translate-x-0'
                  } pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out`}
                />
              </button>
            </div>

            <div className="flex items-center justify-between">
              <div>
                <span className="text-sm font-medium text-gray-900 dark:text-gray-300">New Orders</span>
                <p className="text-sm text-gray-500">Get notified when you receive new orders</p>
              </div>
              <button
                type="button"
                onClick={() => handleNotificationToggle('newOrders')}
                className={`${
                  notifications.newOrders
                    ? 'bg-indigo-600'
                    : 'bg-gray-200 dark:bg-gray-700'
                } relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2`}
              >
                <span
                  className={`${
                    notifications.newOrders ? 'translate-x-5' : 'translate-x-0'
                  } pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out`}
                />
              </button>
            </div>

            <div className="flex items-center justify-between">
              <div>
                <span className="text-sm font-medium text-gray-900 dark:text-gray-300">Weekly Reports</span>
                <p className="text-sm text-gray-500">Receive weekly performance reports</p>
              </div>
              <button
                type="button"
                onClick={() => handleNotificationToggle('reports')}
                className={`${
                  notifications.reports
                    ? 'bg-indigo-600'
                    : 'bg-gray-200 dark:bg-gray-700'
                } relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2`}
              >
                <span
                  className={`${
                    notifications.reports ? 'translate-x-5' : 'translate-x-0'
                  } pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out`}
                />
              </button>
            </div>
          </div>
        </div>

        {/* Save Button */}
        <div className="flex justify-end">
          <button
            type="button"
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
          >
            Save Changes
          </button>
        </div>
      </div>
    </div>
  );
} 